import { ApiService } from "./api.service";

export type TPermissionCheck = {
  action: string;
  /** Existing instance the action targets (read/edit/delete). Omit for create
   *  and workspace-level actions. */
  resource_id?: string;
  /** Only meaningful for `*.create`: target team to create under. Omit to ask
   *  about a workspace-level create. The resource type is derived server-side
   *  from the action prefix. */
  team_id?: string;
};

export type TPermissionCheckResult = {
  action: string;
  resource_id: string | null;
  can: boolean;
};

/** Most `checks` the endpoint accepts in one request — it 400s above this
 *  (`core/api/views_permissions.py` `_MAX_CHECKS`). Callers ask about a *page* of
 *  rows, which is 100 teams at `TEAMS_PAGE_SIZE`, so batching is the caller's
 *  problem unless a helper owns it: {@link PermissionService.canByResource}
 *  does. */
export const MAX_CHECKS_PER_REQUEST = 25;

export class PermissionService extends ApiService {
  async can(slug: string, checks: TPermissionCheck[]): Promise<TPermissionCheckResult[]> {
    return this.post<{ results: TPermissionCheckResult[] }>(
      `/api/workspaces/${slug}/permissions/can/`,
      { checks },
    ).then((res) => res.results);
  }

  /**
   * Resolve every `actions` × `resourceIds` verdict, and return the ids allowed
   * per action — `result.get("team.edit")` being the set of teams this user may
   * edit.
   *
   * Asking per action matters: two verbs on the same row are genuinely different
   * answers, not one permission with a label. A team lead may edit their own
   * team *and* every team beneath it, while they may delete only their own — so
   * deriving one verb from the other is wrong in both directions (NG-1150).
   *
   * Requests are split to stay under the endpoint's own cap and issued together.
   * It rejects if ANY chunk fails, deliberately: a caller gating an affordance
   * must fail closed on the whole page rather than show a half-resolved set that
   * looks like a denial for the rows whose chunk didn't land.
   */
  async canByResource(
    slug: string,
    actions: readonly string[],
    resourceIds: readonly string[],
  ): Promise<Map<string, ReadonlySet<string>>> {
    const allowed = new Map<string, Set<string>>(actions.map((action) => [action, new Set()]));
    if (actions.length === 0 || resourceIds.length === 0) return allowed;

    const checks: TPermissionCheck[] = resourceIds.flatMap((resource_id) =>
      actions.map((action) => ({ action, resource_id })),
    );
    const chunks: TPermissionCheck[][] = [];
    for (let i = 0; i < checks.length; i += MAX_CHECKS_PER_REQUEST) {
      chunks.push(checks.slice(i, i + MAX_CHECKS_PER_REQUEST));
    }

    const settled = await Promise.all(chunks.map((chunk) => this.can(slug, chunk)));
    for (const result of settled.flat()) {
      if (result.can && result.resource_id) allowed.get(result.action)?.add(result.resource_id);
    }
    return allowed;
  }
}
